import { useRef, useState, useEffect } from 'react';
import { useBluetoothPrinter } from '@/hooks/useBluetoothPrinter';
import { Loan, Customer } from '@/hooks/useSupabaseStore';
import { useShop } from '@/hooks/useShop';
import { APP_NAME } from '@/lib/brand';

interface Props {
  loan: Loan;
  customers?: Customer[];
  onClose: () => void;
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
}

function numberToWords(n: number): string {
  if (n === 0) return 'Không đồng';
  const units = ['','một','hai','ba','bốn','năm','sáu','bảy','tám','chín'];
  const teens = ['mười','mười một','mười hai','mười ba','mười bốn','mười lăm','mười sáu','mười bảy','mười tám','mười chín'];
  function rg(num: number): string {
    if (num === 0) return '';
    if (num < 10) return units[num];
    if (num < 20) return teens[num - 10];
    if (num < 100) { const t=Math.floor(num/10),u=num%10; return units[t]+' mươi'+(u>0?' '+(u===5?'lăm':units[u]):''); }
    const h=Math.floor(num/100),r=num%100;
    return units[h]+' trăm'+(r>0?' '+rg(r):'');
  }
  const B=Math.floor(n/1e9),M=Math.floor((n%1e9)/1e6),K=Math.floor((n%1e6)/1e3),R=n%1e3;
  let s='';
  if(B>0) s+=rg(B)+' tỷ ';
  if(M>0) s+=rg(M)+' triệu ';
  if(K>0) s+=rg(K)+' nghìn ';
  if(R>0) s+=rg(R);
  return s.trim().charAt(0).toUpperCase()+s.trim().slice(1)+' đồng';
}

// Tạo chuỗi dấu chấm lấp đầy dòng
const dots = (n = 60) => '.'.repeat(n);

// Bỏ dấu tiếng Việt cho máy in nhiệt Bluetooth (chỉ in được ASCII)
const noDiacritics = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D');

export default function PrintLoanTicket({ loan, customers = [], onClose }: Props) {
  const bluetooth = useBluetoothPrinter();
  const { shop } = useShop();
  // Thông tin Bên A: ưu tiên cấu hình riêng cho phiếu in, fallback thông tin tiệm
  const shopName = shop?.printName || shop?.name || APP_NAME;
  const shopAddress = [shop?.printAddress1 || shop?.address, shop?.printAddress2]
    .filter(Boolean)
    .join(', ');
  const shopPhone = shop?.phone || '';
  const [btMessage, setBtMessage] = useState('');

  // Tự động tìm thông tin khách hàng từ danh sách
  const matchedCustomer = customers.find(c => c.id === loan.customerId);

  const [customerName, setCustomerName] = useState(loan.customerName || '');
  const [customerPhone, setCustomerPhone] = useState(loan.customerPhone || '');
  const [assetName, setAssetName] = useState(loan.assetName || '');
  const [cccd, setCccd] = useState(matchedCustomer?.idCard || '');
  const [cccdDate, setCccdDate] = useState('');
  const [hktt, setHktt] = useState(matchedCustomer?.address || '');
  const [brand, setBrand] = useState('');
  const [frameNo, setFrameNo] = useState('');
  const [engineNo, setEngineNo] = useState('');

  const [contractNo, setContractNo] = useState(loan.id || '');
  const [cccdFront, setCccdFront] = useState<string | null>(null);
  const [cccdBack, setCccdBack] = useState<string | null>(null);
  const cccdFrontRef = useRef<HTMLInputElement>(null);
  const cccdBackRef = useRef<HTMLInputElement>(null);

  const storageKey = `pawn_ticket_edit_${loan.id}`;

  // Load saved edits from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved.customerName !== undefined) setCustomerName(saved.customerName);
        if (saved.customerPhone !== undefined) setCustomerPhone(saved.customerPhone);
        if (saved.assetName !== undefined) setAssetName(saved.assetName);
        if (saved.cccd !== undefined) setCccd(saved.cccd);
        if (saved.cccdDate !== undefined) setCccdDate(saved.cccdDate);
        if (saved.hktt !== undefined) setHktt(saved.hktt);
        if (saved.brand !== undefined) setBrand(saved.brand);
        if (saved.frameNo !== undefined) setFrameNo(saved.frameNo);
        if (saved.engineNo !== undefined) setEngineNo(saved.engineNo);
        if (saved.contractNo !== undefined) setContractNo(saved.contractNo);
        if (saved.cccdFront !== undefined) setCccdFront(saved.cccdFront);
        if (saved.cccdBack !== undefined) setCccdBack(saved.cccdBack);
      }
    } catch {
      // ignore corrupted storage
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loan.id]);

  // Auto-save edits to localStorage whenever fields change
  useEffect(() => {
    const data = {
      customerName,
      customerPhone,
      assetName,
      cccd,
      cccdDate,
      hktt,
      brand,
      frameNo,
      engineNo,
      contractNo,
      cccdFront,
      cccdBack,
    };
    try {
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch {
      // ignore quota errors
    }
  }, [customerName, customerPhone, assetName, cccd, cccdDate, hktt, brand, frameNo, engineNo, contractNo, cccdFront, cccdBack, storageKey]);

  const startD = new Date(loan.startDate);
  const dueD = new Date(loan.dueDate);
  const days = Math.max(1, Math.ceil((dueD.getTime() - startD.getTime()) / 86400000));
  const interestPerMonth = Math.round(loan.loanAmount * loan.interestRate / 100);
  const totalInterest = Math.round(interestPerMonth * days / 30);
  const totalPayback = loan.loanAmount + totalInterest;

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, setter: (v: string | null) => void) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setter(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handlePrint = () => {
    // Điền giá trị vào dấu chấm — nếu có giá trị thì hiện, không thì để chấm
    const fill = (val: string, totalDots = 40) => {
      if (!val) return dots(totalDots);
      return val + dots(Math.max(2, totalDots - val.length));
    };

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8">
<title>Hợp Đồng Cầm Đồ</title>
<style>
  @page {
    size: 148mm 210mm portrait;
    margin: 0;
  }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body {
    width: 148mm;
    height: 210mm;
    overflow: hidden;
    font-family: 'Times New Roman', Times, serif;
    color: #cc0000;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  b, strong { font-weight: 700; }

  .page {
    width: 148mm;
    height: 210mm;
    padding: 5mm 8mm;
    display: flex;
    flex-direction: column;
  }

  /* ===== HEADER ===== */
  .hdr { display: table; width: 100%; font-size: 8.5pt; margin-bottom: 2pt; line-height: 1.35; }
  .hdr-l { display: table-cell; width: 38%; vertical-align: top; }
  .hdr-r { display: table-cell; width: 62%; vertical-align: top; text-align: center; }

  /* ===== ĐƯỜNG KẺ ===== */
  .hline { border-top: 0.6pt solid #cc0000; margin: 2pt 0; }
  .divider { border-top: 0.8pt dashed #cc0000; margin: 3pt 0; }

  /* ===== TIÊU ĐỀ ===== */
  .title { text-align: center; font-size: 13pt; font-weight: 900; letter-spacing: 1pt; margin: 2pt 0 1pt; line-height: 1.2; }
  .lien  { text-align: center; font-style: italic; font-size: 9pt; margin-bottom: 3pt; }

  /* ===== THÔNG TIN ===== */
  .row { font-size: 10pt; line-height: 1.4; margin-bottom: 1pt; }

  /* ===== CAM KẾT ===== */
  .cam-title { text-align: center; font-size: 10pt; font-weight: 900; letter-spacing: 0.3pt; margin: 3pt 0 2pt; }
  .cam-txt { font-size: 9pt; text-align: justify; line-height: 1.32; margin-bottom: 2pt; }

  /* ===== CHỮ KÝ ===== */
  .sig { display: table; width: 100%; margin-top: 3pt; margin-bottom: 3pt; table-layout: fixed; }
  .sig-c { display: table-cell; width: 50%; text-align: center; font-size: 9.5pt; }
  .sig-c .it { font-style: italic; font-size: 8.5pt; }
  .sig-c .sp { height: 20pt; }

  /* ===== PHỤ LỤC ===== */
  .pl-title { text-align: center; font-size: 9.5pt; font-weight: 700; margin-bottom: 2pt; }
  .pl-row { font-size: 9pt; line-height: 1.35; margin-bottom: 1pt; }
</style>
</head>
<body>
<div class="page">

<div class="hdr">
  <div class="hdr-l">
    <div><b>${shopName}</b></div>
    <div>${shopAddress || ''}</div>
    <div>${shopPhone ? `SĐT: ${shopPhone}` : ''}</div>
  </div>
  <div class="hdr-r">
    <div><b>CỘNG HOÀ XÃ HỘI CHỦ NGHĨA VIỆT NAM</b></div>
    <div>Độc lập - Tự do - Hạnh phúc</div>
    <div>--------o0o--------</div>
  </div>
</div>

<div class="hline"></div>
<div class="title">HỢP ĐỒNG CẦM ĐỒ</div>
<div class="lien">(Liên: ${fill(contractNo, 20)})</div>

<div class="row"><b>Bên A (Nhận cầm cố):</b> ${shopName}${shopAddress ? ` — ${shopAddress}` : ''}</div>
<div class="row"><b>Bên B (Cầm cố):</b> ${fill(customerName, 46)}</div>
<div class="row"><b>CCCD số:</b> ${fill(cccd, 22)} &nbsp;<b>Cấp ngày:</b> ${fill(cccdDate, 16)}</div>
<div class="row"><b>Đăng ký HKTT:</b> ${fill(hktt, 50)}</div>
<div class="row"><b>Tài sản cầm cố:</b> ${fill(assetName, 25)} &nbsp;<b>Nhãn hiệu:</b> ${fill(brand, 11)}</div>
<div class="row"><b>Số máy:</b> ${fill(engineNo, 27)} &nbsp;<b>Số khung:</b> ${fill(frameNo, 11)}</div>
<div class="row"><b>Số tiền cầm:</b> ${fill(loan.loanAmount.toLocaleString('vi-VN') + ' đồng', 20)} &nbsp;<b>(Bằng chữ:</b> ${fill(numberToWords(loan.loanAmount), 28)})</div>
<div class="row"><b>Từ ngày:</b> ${fill(formatDate(loan.startDate), 11)} &nbsp;<b>đến ngày:</b> ${fill(formatDate(loan.dueDate), 11)} <b>hết hạn.</b></div>
<div class="row"><b>Lãi suất/tháng:</b> ${fill(loan.interestRate + '%', 48)}</div>

<div class="cam-title">CAM KẾT CHỊU TRÁCH NHIỆM ĐÔI BÊN</div>
<p class="cam-txt"><b>BÊN B:</b> Chịu trách nhiệm pháp lý về sở hữu hợp pháp tài sản cầm cố; trả nợ gốc, lãi đầy đủ đúng hạn. Hết hạn phải trả đủ gốc + lãi + chi phí (nếu có) để nhận lại tài sản. Quá hạn, Bên A toàn quyền xử lý tài sản. Mất hợp đồng phải báo ngay Bên A và có xác nhận chính quyền địa phương; báo trễ mọi thiệt hại Bên B chịu hoàn toàn.</p>
<p class="cam-txt"><b>BÊN A:</b> Bảo quản tài sản và giấy tờ kèm theo đúng hiện trạng ban đầu; hoàn trả đầy đủ cho Bên B sau khi Bên B thanh toán xong gốc và lãi theo thoả thuận. Hợp đồng lập 02 bản, mỗi bên giữ 01 bản, có giá trị pháp lý như nhau.</p>

<div class="sig">
  <div class="sig-c">
    <div><b>BÊN CẦM CỐ (BÊN B)</b></div>
    <div class="it">(Ký và ghi rõ họ tên)</div>
    <div class="sp"></div>
  </div>
  <div class="sig-c">
    <div><b>BÊN NHẬN CẦM CỐ (BÊN A)</b></div>
    <div class="it">(Ký và ghi rõ họ tên)</div>
    <div class="sp"></div>
  </div>
</div>

<div class="divider"></div>
<div class="pl-title">PHỤ LỤC HỢP ĐỒNG: Gia hạn</div>
<div class="pl-row">Vay Thêm Lần 1${dots(28)} &nbsp;&nbsp; Vay Thêm Lần 2${dots(28)}</div>

</div>
</body>
</html>`;

    const old = document.getElementById('_hd_print_frame');
    if (old) old.remove();
    const iframe = document.createElement('iframe');
    iframe.id = '_hd_print_frame';
    iframe.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;border:none;opacity:0;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) return;
    doc.open(); doc.write(html); doc.close();
    iframe.onload = () => {
      setTimeout(() => {
        const win = iframe.contentWindow;
        if (!win) return;
        // Ép portrait trước khi mở hộp thoại in
        try {
          (win.screen.orientation as unknown as { lock: (o: string) => Promise<void> })
            .lock('portrait').catch(() => {});
        } catch (_) { /* ignore nếu browser không hỗ trợ */ }
        win.focus();
        win.print();
        setTimeout(() => iframe.remove(), 3000);
      }, 250);
    };
  };

  const handleBluetoothPrint = async () => {
    setBtMessage('');
    try {
      if (!bluetooth.connected) {
        setBtMessage('Đang kết nối máy in Bluetooth...');
        await bluetooth.connect();
        setBtMessage('Đã kết nối — Đang in...');
      }

      await bluetooth.printPawnTicket({
        shopName: noDiacritics(shopName),
        shopAddress: noDiacritics(shopAddress),
        shopPhone,
        contractNo,
        customerName,
        customerPhone,
        cccd,
        cccdDate,
        hktt,
        assetName,
        brand,
        engineNo,
        frameNo,
        loanAmount: loan.loanAmount,
        loanAmountWords: numberToWords(loan.loanAmount),
        startDate: formatDate(loan.startDate),
        dueDate: formatDate(loan.dueDate),
        interestRate: loan.interestRate,
        interestPerMonth,
        totalInterest,
        totalPayback,
        days,
      });

      setBtMessage('In thành công!');
      setTimeout(() => setBtMessage(''), 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Lỗi in Bluetooth';
      setBtMessage('Lỗi: ' + msg);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg">
              <i className="ri-file-text-line text-amber-600"></i>
            </div>
            <div>
              <h3 className="font-bold text-gray-800 text-sm">Phiếu Cầm Đồ</h3>
              <p className="text-xs text-gray-400">Khổ A5 (148×210mm) — 1 tờ</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handlePrint}
              className="flex items-center gap-1.5 px-4 py-2 bg-red-700 text-white rounded-lg text-sm font-semibold hover:bg-red-800 transition-colors cursor-pointer whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-printer-line"></i></span>
              In phiếu (A5)
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
              <i className="ri-close-line text-gray-500 text-lg"></i>
            </button>
          </div>
        </div>

        {/* Bluetooth print section */}
        <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center bg-blue-100 rounded-lg">
                <i className="ri-bluetooth-line text-blue-600 text-sm"></i>
              </div>
              <div>
                <p className="text-xs font-bold text-blue-700">In Bluetooth</p>
                <p className="text-[10px] text-blue-500">
                  {bluetooth.connected
                    ? `Đã kết nối: ${bluetooth.device?.name || 'Máy in Bluetooth'}`
                    : bluetooth.isSupported
                      ? 'Chưa kết nối máy in Bluetooth'
                      : 'Trình duyệt không hỗ trợ Bluetooth'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {bluetooth.connected && (
                <button onClick={bluetooth.disconnect}
                  className="flex items-center gap-1 px-3 py-1.5 border border-blue-300 text-blue-600 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors cursor-pointer whitespace-nowrap">
                  <span className="w-3 h-3 flex items-center justify-center"><i className="ri-close-line"></i></span>
                  Ngắt kết nối
                </button>
              )}
              <button onClick={handleBluetoothPrint}
                disabled={bluetooth.connecting || !bluetooth.isSupported}
                className="flex items-center gap-1 px-4 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed">
                <span className="w-3 h-3 flex items-center justify-center">
                  {bluetooth.connecting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-bluetooth-connect-line"></i>}
                </span>
                {bluetooth.connecting ? 'Đang kết nối...' : bluetooth.connected ? 'In Bluetooth' : 'Kết nối & In'}
              </button>
            </div>
          </div>
          {btMessage && (
            <p className={`text-xs mt-2 ${btMessage.includes('Lỗi') || btMessage.includes('Không') ? 'text-red-500' : 'text-blue-600'}`}>
              {btMessage}
            </p>
          )}
        </div>

        {/* Form nhập liệu */}
        <div className="px-6 py-4 bg-amber-50 border-b border-amber-100">
          <p className="text-xs font-bold text-amber-700 mb-3 uppercase tracking-wide">Chỉnh sửa thông tin trước khi in</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Tên khách hàng (Bên B)', val: customerName, set: setCustomerName, ph: 'Nhập tên...' },
              { label: 'Số điện thoại', val: customerPhone, set: setCustomerPhone, ph: 'SĐT...' },
              { label: 'Tên tài sản cầm cố', val: assetName, set: setAssetName, ph: 'Tên tài sản...' },
              { label: 'Nhãn hiệu / Model', val: brand, set: setBrand, ph: 'Honda, Samsung...' },
              { label: 'Số CCCD', val: cccd, set: setCccd, ph: 'Số CCCD...' },
              { label: 'Ngày cấp CCCD', val: cccdDate, set: setCccdDate, ph: '01/01/2020' },
              { label: 'Số khung / IMEI', val: frameNo, set: setFrameNo, ph: 'Số khung...' },
              { label: 'Số máy', val: engineNo, set: setEngineNo, ph: 'Số máy...' },

              { label: 'Đăng ký HKTT', val: hktt, set: setHktt, ph: 'Địa chỉ hộ khẩu...' },
              { label: 'Số hợp đồng (Liên)', val: contractNo, set: setContractNo, ph: 'Số liên...' },
            ].map(f => (
              <div key={f.label}>
                <label className="text-xs text-gray-600 font-medium block mb-1">{f.label}</label>
                <input value={f.val} onChange={e => f.set(e.target.value)} placeholder={f.ph}
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-red-400" />
              </div>
            ))}
          </div>

          {/* CCCD Upload */}
          <div className="mt-4 pt-3 border-t border-amber-200">
            <p className="text-xs font-bold text-amber-700 mb-2 uppercase tracking-wide">Ảnh CCCD (lưu nội bộ, không in)</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Mặt trước', val: cccdFront, set: setCccdFront, ref: cccdFrontRef },
                { label: 'Mặt sau', val: cccdBack, set: setCccdBack, ref: cccdBackRef },
              ].map((img, i) => (
                <div key={i}>
                  <label className="text-xs text-gray-500 font-medium block mb-1">{img.label}</label>
                  <div className="relative border-2 border-dashed border-amber-300 rounded-xl overflow-hidden bg-white cursor-pointer hover:border-red-400 transition-colors h-20"
                    onClick={() => img.ref.current?.click()}>
                    {img.val ? (
                      <>
                        <img src={img.val} alt={img.label} className="w-full h-full object-cover" />
                        <button className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center bg-red-600 text-white rounded-full text-xs cursor-pointer"
                          onClick={e => { e.stopPropagation(); img.set(null); if (img.ref.current) img.ref.current.value = ''; }}>
                          <i className="ri-close-line"></i>
                        </button>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full gap-1 text-amber-400">
                        <span className="w-6 h-6 flex items-center justify-center"><i className="ri-camera-line text-xl"></i></span>
                        <span className="text-xs text-gray-400">Tải ảnh lên</span>
                      </div>
                    )}
                  </div>
                  <input ref={img.ref} type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(e, img.set)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="px-6 py-4">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Xem trước</p>

          {/* Khung mô phỏng tờ A5 — tỉ lệ 148:210 */}
          <div className="border-2 border-red-200 rounded-xl bg-white overflow-hidden"
            style={{ fontFamily: 'Times New Roman, serif', color: '#cc0000', fontSize: 9, lineHeight: 1.35 }}>

            <div className="p-4">

              {/* Header */}
              <div className="flex justify-between mb-1" style={{ fontSize: 8 }}>
                <div>
                  <p className="font-bold">{shopName}</p>
                  {shopAddress ? <p>{shopAddress}</p> : null}
                  {shopPhone ? <p>SĐT: {shopPhone}</p> : null}
                </div>
                <div className="text-center" style={{ fontSize: 8 }}>
                  <p className="font-bold">CỘNG HOÀ XÃ HỘI CHỦ NGHĨA VIỆT NAM</p>
                  <p className="italic">Độc lập - Tự do - Hạnh phúc</p>
                  <p>--------o0o--------</p>
                </div>
              </div>

              {/* Đường kẻ */}
              <div className="border-t border-red-400 my-1" />

              {/* Tiêu đề */}
              <p className="text-center font-black tracking-widest my-1" style={{ fontSize: 14 }}>HỢP ĐỒNG CẦM ĐỒ</p>
              <p className="text-center italic mb-2" style={{ fontSize: 8.5 }}>(Liên: {contractNo || '...........'})</p>

              {/* Thông tin hợp đồng */}
              <div style={{ fontSize: 9 }} className="space-y-0.5">
                <p><strong>Bên A (Nhận cầm cố):</strong> {shopName}{shopAddress ? ` — ${shopAddress}` : ''}</p>
                <p><strong>Bên B (Cầm cố):</strong> <span className="underline decoration-dotted">{customerName || '......................................'}</span></p>
                <p>
                  <strong>CCCD số:</strong> <span className="underline decoration-dotted">{cccd || '................'}</span>
                  &nbsp;<strong>Cấp ngày:</strong> <span className="underline decoration-dotted">{cccdDate || '...........'}&nbsp;&nbsp;&nbsp;&nbsp;</span>
                </p>
                <p><strong>Đăng ký HKTT:</strong> <span className="underline decoration-dotted">{hktt || '................................................'}</span></p>
                <p>
                  <strong>Tài sản cầm cố:</strong> <span className="underline decoration-dotted">{assetName || '..................'}</span>
                  &nbsp;<strong>Nhãn hiệu:</strong> <span className="underline decoration-dotted">{brand || '.........'}</span>
                </p>
                <p>
                  <strong>Số máy:</strong> <span className="underline decoration-dotted">{engineNo || '..................'}</span>
                  &nbsp;<strong>Số khung:</strong> <span className="underline decoration-dotted">{frameNo || '.........'}</span>
                </p>
                <p>
                  <strong>Số tiền cầm:</strong> <span className="underline decoration-dotted">{loan.loanAmount.toLocaleString('vi-VN')} đồng</span>
                  &nbsp;&nbsp;<strong>(Bằng chữ:</strong>&nbsp;<span className="underline decoration-dotted">{numberToWords(loan.loanAmount)}</span>)
                </p>
                <p>
                  <strong>Từ ngày:</strong> <span className="underline decoration-dotted">{formatDate(loan.startDate)}</span>
                  &nbsp;<strong>đến ngày:</strong> <span className="underline decoration-dotted">{formatDate(loan.dueDate)}</span>
                  &nbsp;<strong>hết hạn.</strong>
                </p>
                <p><strong>Lãi suất/tháng:</strong> <span className="underline decoration-dotted">{loan.interestRate}%</span></p>
              </div>

              {/* Cam kết */}
              <p className="text-center font-black my-1.5" style={{ fontSize: 9 }}>CAM KẾT CHỊU TRÁCH NHIỆM ĐÔI BÊN</p>
              <p className="text-justify mb-1" style={{ fontSize: 8, lineHeight: 1.32 }}>
                <strong>BÊN B:</strong> Chịu trách nhiệm pháp lý về sở hữu hợp pháp tài sản cầm cố; trả nợ gốc, lãi đầy đủ đúng hạn. Hết hạn phải trả đủ gốc + lãi + chi phí (nếu có) để nhận lại tài sản. Quá hạn, Bên A toàn quyền xử lý tài sản. Mất hợp đồng phải báo ngay Bên A và có xác nhận chính quyền địa phương; báo trễ mọi thiệt hại Bên B chịu hoàn toàn.
              </p>
              <p className="text-justify mb-1" style={{ fontSize: 8, lineHeight: 1.32 }}>
                <strong>BÊN A:</strong> Bảo quản tài sản và giấy tờ kèm theo đúng hiện trạng ban đầu; hoàn trả đầy đủ cho Bên B sau khi Bên B thanh toán xong gốc và lãi theo thoả thuận. Hợp đồng lập 02 bản, mỗi bên giữ 01 bản, có giá trị pháp lý như nhau.
              </p>

              {/* Chữ ký */}
              <div className="flex justify-around mt-2 mb-2 text-center" style={{ fontSize: 8.5 }}>
                <div>
                  <p className="font-bold">BÊN CẦM CỐ (BÊN B)</p>
                  <p className="italic" style={{ fontSize: 8 }}>(Ký và ghi rõ họ tên)</p>
                  <div style={{ height: 26 }} />
                </div>
                <div>
                  <p className="font-bold">BÊN NHẬN CẦM CỐ (BÊN A)</p>
                  <p className="italic" style={{ fontSize: 8 }}>(Ký và ghi rõ họ tên)</p>
                  <div style={{ height: 26 }} />
                </div>
              </div>

              {/* Phụ lục */}
              <div className="border-t border-dashed border-red-400 my-1.5" />
              <p className="text-center font-bold mb-1" style={{ fontSize: 8.5 }}>PHỤ LỤC HỢP ĐỒNG: Gia hạn</p>
              <div style={{ fontSize: 8 }} className="space-y-0.5">
                <p>Vay Thêm Lần 1........................ &nbsp;&nbsp; Vay Thêm Lần 2........................</p>
              </div>

            </div>
          </div>

          <p className="text-xs text-gray-400 mt-2 text-center">Nhấn "In phiếu (A5)" → chọn khổ A5 + Portrait trong hộp thoại in</p>
        </div>

      </div>
    </div>
  );
}